import Select from 'react-select';
import { useTranslate } from 'hooks/useTranslate';
import normalizeCif from 'utils/normalizeCif';
import EmptyStateForTable from 'modules/_shared/components/EmptyStates/EmptyStateForTable';
import { TablePagination } from 'modules/_shared/Table/components/TablePagination';

const SyncNotMatchingTable = ({
  notMatchingPaginated,
  firstNotMatchingNr,
  notMatchingList,
  notMatchingPagination,
  employeeOptions,
  notMatchingSelections,
  matchingEmployeeIds = [],
  alreadyLinkedEmployeeIds = [],
  setNotMatchingPagination,
  onEmployeeSelect,
}) => {
  const { t } = useTranslate();

  const selectedCount = notMatchingList.filter(
    (b) => notMatchingSelections[b._id]
  ).length;
  const totalCount = notMatchingList.length;

  const getDisabledEmployeeIds = (currentBeneficiary) => {
    const normalizedCif = normalizeCif(currentBeneficiary?.cif || '');
    const disabled = new Set([
      ...matchingEmployeeIds,
      ...alreadyLinkedEmployeeIds,
    ]);
    notMatchingList.forEach((b) => {
      const sel = notMatchingSelections[b._id];
      if (sel && normalizeCif(b.cif || '') !== normalizedCif) {
        disabled.add(sel.value);
      }
    });
    return disabled;
  };

  return (
    <>
      <p className="text-soft px-4 mb-3">
        {t('SelectEmployeeForBeneficiary')}.&nbsp;
        {t('SelectEmployeeSelectionCount', {
          count: selectedCount,
          total: totalCount,
        })}
      </p>

      <div className="border-top border-bottom">
        <table className="table table-striped-inverse align-middle">
          <thead>
            <tr className="nk-tb-item tb-tnx-head">
              <th className="nk-tb-col align-middle">{t('Nr')}</th>
              <th className="nk-tb-col align-middle">{t('Beneficiary')}</th>
              <th className="nk-tb-col align-middle">{t('Cif')}</th>
              <th className="nk-tb-col align-middle">{t('Employee')}</th>
              <th className="nk-tb-col align-middle">
                {t('FactorialEmployeeId')}
              </th>
            </tr>
          </thead>
          {notMatchingList.length === 0 ? (
            <EmptyStateForTable
              mainMessage={t('SyncNotMatchingEmptyMainMessage')}
              infoMessage={t('SyncNotMatchingEmptyInfoMessage')}
              colspan={5}
            />
          ) : (
            <tbody>
              {notMatchingPaginated.map((beneficiary, index) => (
                <tr key={beneficiary._id} className="nk-tb-item">
                  <td className="nk-tb-col align-middle">
                    {firstNotMatchingNr + index}
                  </td>
                  <td className="nk-tb-col align-middle">
                    <div className="text-base">{beneficiary.name}</div>
                    {beneficiary.email && (
                      <small className="text-soft d-block">
                        {beneficiary.email}
                      </small>
                    )}
                  </td>
                  <td className="nk-tb-col align-middle">
                    <span className="text-base">{beneficiary.cif || '—'}</span>
                  </td>
                  <td className="nk-tb-col align-middle w-300px">
                    <Select
                      options={employeeOptions}
                      value={notMatchingSelections[beneficiary._id] || null}
                      onChange={(opt) => onEmployeeSelect(beneficiary._id, opt)}
                      isClearable
                      placeholder={t('SelectEmployee')}
                      className="react-select"
                      isOptionDisabled={(option) => {
                        const disabled = getDisabledEmployeeIds(beneficiary);
                        const currentSelection = notMatchingSelections[beneficiary._id]?.value;
                        if (option.value === currentSelection) return false;
                        return disabled.has(option.value);
                      }}
                    />
                  </td>
                  <td className="nk-tb-col align-middle">
                    <span className="text-base">
                      {notMatchingSelections[beneficiary._id]?.externalId ??
                        '—'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          )}
        </table>
      </div>

      <div className="px-4">
        {notMatchingList.length > 0 && (
          <TablePagination
            totalRows={notMatchingList.length}
            defaultPage={notMatchingPagination.page}
            defaultRowsPerPage={notMatchingPagination.rowsPerPage}
            onChangePage={(page) =>
              setNotMatchingPagination((p) => ({ ...p, page }))
            }
            onChangeRowsPerPage={(rowsPerPage) =>
              setNotMatchingPagination({ page: 1, rowsPerPage })
            }
          />
        )}
      </div>
    </>
  );
};

export default SyncNotMatchingTable;
